function saleCardFormatMoney(cents) {
  const value = cents / 100;
  return value % 1 === 0 ? `$${value}` : `$${value.toFixed(2)}`;
}

function saleCardOrdinal(day) {
  const j = day % 10;
  const k = day % 100;
  if (j === 1 && k !== 11) return `${day}st`;
  if (j === 2 && k !== 12) return `${day}nd`;
  if (j === 3 && k !== 13) return `${day}rd`;
  return `${day}th`;
}

function saleCardFormatDate(iso) {
  if (!iso) return '';
  // Parse YYYY-MM-DD without timezone drift
  const parts = String(iso).split('-');
  if (parts.length < 3) return iso;
  const months = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  const month = months[parseInt(parts[1], 10) - 1];
  const day = parseInt(parts[2], 10);
  if (!month || !day) return iso;
  return `${month} ${saleCardOrdinal(day)}`;
}

function saleCardEtaLabel(from, to) {
  if (!from || !to) return '';
  return `Estimated shipping window between ${saleCardFormatDate(from)} and ${saleCardFormatDate(to)}`;
}

function saleCardEtaHtml(from, to) {
  if (!from || !to) return '';
  return `Estimated shipping window between <strong>${saleCardFormatDate(
    from
  )}</strong> and <strong>${saleCardFormatDate(to)}</strong>`;
}

function saleCardWhenReady(fn) {
  // Custom elements can be upgraded before their children are parsed.
  // Defer child-dependent init until the DOM is fully parsed.
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', fn, { once: true });
  } else {
    fn();
  }
}

class SaleProductCardForm extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    saleCardWhenReady(() => this._init());
  }

  _init() {
    this.form = this.querySelector('form');
    if (!this.form) return;
    this.submitButton = this.form.querySelector('.product-selector__submit');
    this.cartDrawer = document.querySelector('cart-drawer');

    this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
  }

  _getSelectedInput() {
    return (
      this.querySelector('[data-sale-variant-id]') ||
      this.querySelector('[name="variant"]')
    );
  }

  onSubmitHandler(event) {
    event.preventDefault();
    this.submitButton.classList.add('disabled');

    const config = fetchConfig('javascript');
    config.headers['X-Requested-With'] = 'XMLHttpRequest';
    config.headers['Content-Type'] = 'application/json';

    const variantInput = this._getSelectedInput();
    const variantId = variantInput ? variantInput.value : null;

    if (!variantId) {
      this.handleErrorMessage('Please select an option.');
      this.submitButton.classList.remove('disabled');
      return;
    }

    const item = { id: variantId, quantity: 1 };

    // Attach pre-order line item properties when the selected variant is a pre-order
    if (variantInput) {
      const from = variantInput.dataset.preorderFrom;
      const to = variantInput.dataset.preorderTo;
      if (from && to) {
        // Single shared stamp (window.SelahPreorder) — byte-identical label across surfaces.
        item.properties = window.SelahPreorder.properties(from, to);
      }
    }

    const requestData = {
      items: [item],
      sections: this.cartDrawer
        .getSectionsToRender()
        .map(section => section.section),
      sections_url: window.location.pathname
    };

    config.body = JSON.stringify(requestData);

    fetch(`${routes.cart_add_url}`, config)
      .then(response => response.json())
      .then(response => {
        if (response.status) {
          this.handleErrorMessage(response.description);
          return;
        }

        this.cartDrawer.renderContents(response);
      })
      .catch(() => {})
      .finally(() => {
        this.submitButton.classList.remove('disabled');
      });
  }

  handleErrorMessage(errorMessage = false) {
    const errorWrapper = this.querySelector('[data-error-wrapper]');
    if (!errorWrapper) return;
    errorWrapper.classList.toggle('hidden', !errorMessage);
    errorWrapper.textContent = errorMessage || '';
  }
}

if (!window.customElements.get('sale-product-card-form')) {
  window.customElements.define('sale-product-card-form', SaleProductCardForm);
}

class SaleProductSelector extends HTMLElement {
  connectedCallback() {
    if (this._initialized) return;
    this._initialized = true;
    saleCardWhenReady(() => this._init());
  }

  _init() {
    this.variants = this._parseVariants();
    this.hasSize = this.dataset.hasSize === 'true';
    this.hasColour = this.dataset.hasColour === 'true';

    this.hiddenInput = this.querySelector('[data-sale-variant-id]');
    this.sizeRadios = Array.from(this.querySelectorAll('[data-size-radio]'));
    this.sizeSelect = this.querySelector('[data-size-select]');
    this.colourInputs = Array.from(this.querySelectorAll('[data-colour-swatch]'));

    this.selectedSize = null;
    this.selectedColour = null;

    this._initSelection();
    this._bind();

    // Normalise the server-rendered ETA + state for the initially selected variant
    const initial = this._resolveVariant();
    if (initial) this._apply(initial, { skipImage: true, skipPrice: true });
  }

  _parseVariants() {
    const el = this.querySelector('[data-sale-variants]');
    if (!el) return [];
    try {
      return JSON.parse(el.textContent.trim());
    } catch (e) {
      return [];
    }
  }

  _card() {
    return this.closest('in-stock-product-card');
  }

  _submitButton() {
    const form = this.closest('sale-product-card-form');
    return form ? form.querySelector('.product-selector__submit') : null;
  }

  _initSelection() {
    if (this.hasSize) {
      const activeRadio = this.sizeRadios.find(r => r.dataset.selected === 'true' || r.checked);
      if (activeRadio) this.selectedSize = activeRadio.value;
      else if (this.sizeSelect) this.selectedSize = this.sizeSelect.value;
      else if (this.sizeRadios[0]) this.selectedSize = this.sizeRadios[0].value;
    }
    if (this.hasColour) {
      const activeColour = this.colourInputs.find(c => c.checked);
      if (activeColour) this.selectedColour = activeColour.dataset.color;
      else if (this.colourInputs[0]) this.selectedColour = this.colourInputs[0].dataset.color;
    }
  }

  _bind() {
    this.sizeRadios.forEach(radio => {
      radio.addEventListener('change', () => {
        this.selectedSize = radio.value;
        this._setActiveSize(this.selectedSize);
        const v = this._resolveVariant();
        if (v) this._apply(v);
      });
    });

    if (this.sizeSelect) {
      this.sizeSelect.addEventListener('change', () => {
        this.selectedSize = this.sizeSelect.value;
        const v = this._resolveVariant();
        if (v) this._apply(v);
      });
    }

    this.colourInputs.forEach(input => {
      input.addEventListener('change', () => {
        this.selectedColour = input.dataset.color;
        // Keep size valid for the newly chosen colour
        if (this.hasSize) {
          const sizes = this.variants
            .filter(v => v.colour === this.selectedColour)
            .map(v => v.size);
          if (sizes.length && !sizes.includes(this.selectedSize)) {
            this.selectedSize = sizes[0];
            this._setActiveSize(this.selectedSize);
          }
        }
        const v = this._resolveVariant();
        if (v) this._apply(v);
      });
    });
  }

  _resolveVariant() {
    return this.variants.find(v =>
      (!this.hasSize || v.size === this.selectedSize) &&
      (!this.hasColour || v.colour === this.selectedColour)
    );
  }

  _setActiveSize(size) {
    this.sizeRadios.forEach(radio => {
      const isMatch = radio.value === size;
      radio.checked = isMatch;
      radio.dataset.selected = isMatch ? 'true' : 'false';
      const label = radio.nextElementSibling;
      if (label) label.classList.toggle('is-active', isMatch);
    });
    if (this.sizeSelect) this.sizeSelect.value = size;
  }

  _apply(variant, options) {
    options = options || {};
    const card = this._card();
    const isPreorder = !!(variant.from && variant.to);

    // Source of truth for cart submit
    if (this.hiddenInput) {
      this.hiddenInput.value = variant.id;
      if (isPreorder) {
        this.hiddenInput.dataset.preorderFrom = variant.from;
        this.hiddenInput.dataset.preorderTo = variant.to;
      } else {
        delete this.hiddenInput.dataset.preorderFrom;
        delete this.hiddenInput.dataset.preorderTo;
      }
    }

    // Colour label
    if (this.hasColour && card) {
      const colourLabel = card.querySelector('[data-selected-colour]');
      if (colourLabel) colourLabel.textContent = variant.colour;
    }

    // Product image
    if (!options.skipImage && variant.image && card) {
      const img = card.querySelector('.in-stock-product-card__media--wrapper img');
      if (img) {
        img.src = variant.image;
        img.srcset = '';
        img.removeAttribute('data-src');
      }
    }

    // Pre-order notice + button text
    if (card) {
      const notice = card.querySelector('[data-preorder-notice]');
      const eta = card.querySelector('[data-preorder-eta]');
      if (notice) notice.style.display = isPreorder ? '' : 'none';
      if (eta) eta.innerHTML = isPreorder ? saleCardEtaHtml(variant.from, variant.to) : '';
    }

    const submitButton = this._submitButton();
    if (submitButton && !submitButton.hasAttribute('disabled')) {
      submitButton.textContent = isPreorder
        ? 'Pre-Order Now'
        : window.variantStrings
        ? window.variantStrings.addToCart
        : 'Add to Cart';
    }

    if (!options.skipPrice) this._renderPrice(variant);
  }

  _renderPrice(variant) {
    const container = document.querySelector(`#Product-Price-${this.dataset.productId}`);
    if (!container) return;

    const priceWrapper = container.querySelector('.price');
    const compareAtPrice = container.querySelector('[data-compare-at-element]');
    const productPrice = container.querySelector('[data-price-element]');
    const savePercent = container.querySelector('[data-save-text]');
    if (!productPrice) return;

    const card = this._card();
    const imageBadge = card ? card.querySelector('[data-sale-badge]') : null;
    const imageBadgeWrapper = imageBadge ? imageBadge.closest('.marquee-tags') : null;

    const onSale = variant.compare_at && variant.compare_at > variant.price;

    if (onSale) {
      const pct = Math.round(((variant.compare_at - variant.price) * 100) / variant.compare_at);
      if (priceWrapper) priceWrapper.classList.add('price--on-sale');
      if (compareAtPrice) {
        compareAtPrice.innerHTML = `<lomoney>${saleCardFormatMoney(variant.compare_at)}</lomoney>`;
        compareAtPrice.style.display = '';
      }
      let saveText = `Save ${pct}%`;
      // data-fs-today is stamped server-side while a Free Ship Friday window is
      // active — every product ships free that day regardless of price.
      if (variant.price >= 15000 || (savePercent && savePercent.dataset.fsToday === '1')) saveText += ' + Free Shipping';
      if (savePercent) savePercent.innerText = saveText;
      // A specific variant is now selected, so its exact discount is definite — show
      // "Save {pct}%". Update the inner swob spans to keep the styled badge intact
      // (falling back to plain text for the simple marquee badge).
      if (imageBadge) {
        const badgeS2 = imageBadge.querySelector('.s2');
        const badgeS1 = imageBadge.querySelector('[data-badge-label]') || imageBadge.querySelector('.s1');
        if (badgeS2) {
          badgeS2.textContent = `${pct}%`;
          if (badgeS1) badgeS1.textContent = 'Save';
        } else {
          imageBadge.textContent = `Save ${pct}%`;
        }
      }
      if (imageBadgeWrapper) imageBadgeWrapper.hidden = false;
    } else {
      if (priceWrapper) priceWrapper.classList.remove('price--on-sale');
      if (compareAtPrice) compareAtPrice.style.display = 'none';
      if (savePercent) savePercent.innerText = '';
      if (imageBadgeWrapper) imageBadgeWrapper.hidden = true;
    }

    productPrice.innerHTML = `<lomoney>${saleCardFormatMoney(variant.price)}</lomoney>`;
  }
}

if (!window.customElements.get('sale-product-selector')) {
  window.customElements.define('sale-product-selector', SaleProductSelector);
}
